const GEOLOCATION_OPTIONS = {
    enableHighAccuracy: true,
    maximumAge: 0,
    timeout: 10000, // 10s
};

export class SensorResult {
    /**
     * @param {Object} options
     * @param {boolean} options.success - Whether the operation succeeded
     * @param {T|null} [options.data] - The result data if successful
     * @param {string|null} [options.error] - Human-readable error message
     */
    constructor({ success, data = null, error = null }) {
        this.success = success;
        this.data = data;
        this.error = error;
    }

    static ok(data = null) {
        return new SensorResult({ success: true, data });
    }

    static fail(error) {
        return new SensorResult({ success: false, error });
    }
}

/**
 * Stateless helper for requesting sensor permissions and wiring up data pipelines.
 */
export default class SensorPermissionHelper {
    /**
     * Start motion sensors and wire accelerometer → pedometer pipeline.
     * @param {Motion} motion
     * @param {Pedometer} pedometer
     * @returns {Promise<SensorResult>}
     */
    static async startMotion(motion, pedometer) {
        if (motion.isListening) {
            return SensorResult.ok();
        }

        const started = await motion.start();
        if (!started) {
            return SensorResult.fail(`Motion permission denied or not supported`);
        }

        // Wire accelerometer → pedometer data pipeline
        motion.addEventListener(`accelerometer`, (e) => pedometer.processAccelerometer(e.detail));
        await pedometer.start();

        return SensorResult.ok();
    }

    /**
     * Start compass orientation listener.
     * @param {Compass} compass
     * @param {number|null} [northAngle]
     * @returns {Promise<SensorResult>}
     */
    static async startCompass(compass, northAngle = null) {
        if (compass.isListening) {
            return SensorResult.ok();
        }

        if (northAngle != null) {
            compass.setNorthAngle(northAngle);
        }

        const started = await compass.start();
        if (!started) {
            return SensorResult.fail(`Compass permission denied or not supported`);
        }

        return SensorResult.ok();
    }

    /**
     * Get current geolocation position (one-shot).
     * @returns {Promise<SensorResult<GeolocationPosition>>}
     */
    static async getCurrentPosition() {
        if (!navigator.geolocation) {
            return SensorResult.fail(`Geolocation not supported`);
        }

        try {
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, GEOLOCATION_OPTIONS);
            });
            return SensorResult.ok(position);
        } catch (err) {
            console.error(`[SensorPermissionHelper] getCurrentPosition error:`, err);
            return SensorResult.fail(err?.code === 1 ? `Geolocation permission denied` : `Geolocation error`);
        }
    }

    /**
     * Start continuous geolocation watch.
     * @param {Function} onPosition - Called with each new GeolocationPosition
     * @param {Function} [onError] - Called on watch errors
     * @param {Object} [options]
     * @param {boolean} [options.skipInitialPosition=false] - When true, skip getCurrentPosition
     * @returns {Promise<SensorResult<{watchId: number, initialPosition: GeolocationPosition|null}>>}
     */
    static async startGeolocationWatch(onPosition, onError, { skipInitialPosition = false } = {}) {
        if (!navigator.geolocation) {
            return SensorResult.fail(`Geolocation not supported`);
        }

        const errorHandler = onError || ((err) => console.error(`[SensorPermissionHelper] Geolocation watch error:`, err));

        const THROTTLE_MS = 500;
        let lastCallTime = 0;
        let trailingTimer = null;
        const throttledOnPosition = (pos) => {
            const now = Date.now();
            const elapsed = now - lastCallTime;
            clearTimeout(trailingTimer);
            if (elapsed >= THROTTLE_MS) {
                lastCallTime = now;
                onPosition(pos);
            } else {
                trailingTimer = setTimeout(() => {
                    lastCallTime = Date.now();
                    onPosition(pos);
                }, THROTTLE_MS - elapsed);
            }
        };

        try {
            const watchId = navigator.geolocation.watchPosition(throttledOnPosition, errorHandler, GEOLOCATION_OPTIONS);

            if (!skipInitialPosition) {
                navigator.geolocation.getCurrentPosition(
                    throttledOnPosition,
                    errorHandler,
                    GEOLOCATION_OPTIONS
                );
            }

            return SensorResult.ok({ watchId, initialPosition: null });
        } catch (err) {
            console.error(`[SensorPermissionHelper] Error starting geolocation watch:`, err);
            try {
                errorHandler(err);
            } catch (callbackErr) {
                console.error(`[SensorPermissionHelper] Error in geolocation error callback:`, callbackErr);
            }

            return SensorResult.fail(err?.code === 1 ? `Geolocation permission denied` : `Geolocation error`);
        }
    }

    /**
     * Stop a geolocation watch by ID.
     * @param {number|null} watchId
     */
    static clearGeolocationWatch(watchId) {
        if (watchId != null && navigator.geolocation) {
            navigator.geolocation.clearWatch(watchId);
        }
    }

    /**
     * Query current browser permission states using navigator.permissions.query.
     * @returns {Promise<{geolocation: string, accelerometer: string}>}
     *   Each value is 'granted' | 'denied' | 'prompt' | 'unavailable'.
     */
    static async queryPermissionStates() {
        const results = {
            geolocation: `unavailable`,
            accelerometer: `unavailable`
        };

        if (typeof navigator === `undefined` || !navigator.permissions) {
            return results;
        }

        const queries = [
            { name: `geolocation`, key: `geolocation` },
            { name: `accelerometer`, key: `accelerometer` }
        ];

        await Promise.all(queries.map(async({ name, key }) => {
                try {
                    const status = await navigator.permissions.query({ name });
                    results[key] = status.state; // 'granted', 'denied', 'prompt'
                } catch (err) {
                    // Permission name not supported by this browser
                    results[key] = `unavailable`;
                }
            })
        );

        return results;
    }

    /**
     * Check whether all required navigation permissions are already granted.
     * Uses the Permissions API where available, with a fallback for sensors
     * that cannot be queried (e.g. accelerometer on iOS).
     *
     * @param {Object} options
     * @param {boolean} [options.needsMotion=true]       - Whether motion/accelerometer is required
     * @param {boolean} [options.needsGeolocation=true]   - Whether geolocation is required
     * @param {boolean} [options.motionActive=false]      - Whether the Motion sensor is already listening (fallback for non-queryable browsers)
     * @returns {Promise<{allGranted: boolean, states: Object, anyDenied: boolean}>}
     */
    static async checkNavigationPermissions({ needsMotion = true, needsGeolocation = true, motionActive = false } = {}) {
        const states = await SensorPermissionHelper.queryPermissionStates();

        let allGranted = true;
        let anyDenied = false;

        if (needsGeolocation) {
            if (states.geolocation !== `granted`) {
                allGranted = false;
            }
            if (states.geolocation === `denied`) {
                anyDenied = true;
            }
        }

        if (needsMotion) {
            if (states.accelerometer === `granted` || motionActive) {
                // Sensor confirmed active
            } else if (states.accelerometer === `denied`) {
                anyDenied = true;
                allGranted = false;
            } else {
                // 'prompt' or 'unavailable' — cannot confirm without trying
                allGranted = false;
            }
        }

        return { allGranted, states, anyDenied };
    }

    /**
     * Request all permissions needed for navigation.
     * @param {Object} options
     * @param {Motion} options.motion
     * @param {Pedometer} options.pedometer
     * @param {Compass} options.compass
     * @param {number|null} [options.northAngle]
     * @param {boolean} [options.requestMotion=true]
     * @param {boolean} [options.requestGeolocation=true]
     * @param {boolean} [options.skipInitialPosition=false] - Skip blocking GPS fix (use when permissions are pre-granted)
     * @param {Function} [options.onPosition] - Geolocation position callback
     * @param {Function} [options.onGeoError] - Geolocation error callback
     * @returns {Promise<SensorResult<{motionStarted: boolean, compassStarted: boolean, geoStarted: boolean, watchId: number|null, initialPosition: GeolocationPosition|null}>>}
     */
    static async startNavigation({ motion, pedometer, compass, northAngle = null, requestMotion = true, requestGeolocation = true, skipInitialPosition = false, onPosition, onGeoError }) {
        let motionStarted = false;
        let compassStarted = false;
        let geoStarted = false;
        let watchId = null;
        let initialPosition = null;

        // Run motion/compass and geolocation in parallel for faster startup
        const motionPromise = requestMotion
            ? (async() => {
                const motionResult = await SensorPermissionHelper.startMotion(motion, pedometer);
                motionStarted = motionResult.success;
                const compassResult = await SensorPermissionHelper.startCompass(compass, northAngle);
                compassStarted = compassResult.success;
            })()
            : Promise.resolve();

        const geoPromise = requestGeolocation
            ? (async() => {
                const geoResult = await SensorPermissionHelper.startGeolocationWatch(onPosition, onGeoError, { skipInitialPosition });
                geoStarted = geoResult.success;
                if (geoResult.success) {
                    watchId = geoResult.data.watchId;
                    initialPosition = geoResult.data.initialPosition;
                }
            })()
            : Promise.resolve();

        await Promise.all([motionPromise, geoPromise]);

        const allOk = (!requestMotion || motionStarted) && (!requestGeolocation || geoStarted);

        return new SensorResult({
            success: allOk,
            data: { motionStarted, compassStarted, geoStarted, watchId, initialPosition },
            error: allOk ? null : `Some sensor permissions were denied`,
        });
    }

    static async isGeolocationGranted() {
        if (typeof navigator === `undefined` || !navigator.permissions) {
            return false;
        }
        try {
            const status = await navigator.permissions.query({ name: `geolocation` });
            return status.state === `granted`;
        } catch (err) {
            return false;
        }
    }
}

